import React from 'react';
import * as mobx from 'mobx';
import { FormattedMessage, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { alertServerError, handleApiException, showNotification } from '../../global/modal/alerts-host/alert-templates';
import { IAlertsService } from '../../../infrastructure/alerts';
import { RolesAction } from '../../../api/roles.dao';
import { COMMON_ITEMS_PER_PAGE } from '../../../constants/pagination.contants';
import { IAccountDetailsDTO, IAccountsDAO } from '../../../api/accounts.dao';
import { AccountGroupsFormDataVM } from './account-groups-form-data.viewmodel';
import { IAccountGroupsDAO } from '../../../api/account-groups.dao';
import { Space } from '../../../constants/html-character-entities';

class AccountGroupsFormVM {
    @mobx.observable
    public accountsList: IAccountDetailsDTO[];
    @mobx.observable
    public isSaving: boolean;

    @mobx.observable
    public isLoading: boolean;

    @mobx.observable
    public groupFormDataVM: AccountGroupsFormDataVM;

    @mobx.observable
    public accountGroupId: number;

    constructor(
        private fetchAccountGroupsList?: () => void,
        private closeEditGroupForm?: (accountGroupId: number) => void,
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly accountGroupsDAO: IAccountGroupsDAO = IoC.Container.get(IAccountGroupsDAO)
    ) {
        this.groupFormDataVM = new AccountGroupsFormDataVM();
        this.isLoading = true;
        this.fetchAccountsList();
    }

    public getErrorMessage(intl: IntlShape): string | null {
        const { showValidationErrors, getNameError, getDescriptionError, serverErrorMessage } = this.groupFormDataVM;
        if (showValidationErrors()) {
            if (getNameError(intl)) {
                return getNameError(intl);
            } else if (getDescriptionError(intl)) {
                return getDescriptionError(intl);
            }
        }
        return serverErrorMessage;
    }

    public initGroup = async (accountGroupId?: number): Promise<void> => {
        this.groupFormDataVM = new AccountGroupsFormDataVM();
        if (accountGroupId) {
            const groupDetails = await this.accountGroupsDAO.getAccountGroupById({ accountGroupId: accountGroupId });
            mobx.runInAction(() => {
                this.accountGroupId = accountGroupId;
            });
            this.groupFormDataVM.setAccountGroup(groupDetails);
        }
        mobx.runInAction(() => {
            this.isSaving = false;
            this.isLoading = false;
        });
    };

    @mobx.action
    public handleSubmit = async (intl: IntlShape, roleAction: string): Promise<boolean> => {
        this.groupFormDataVM.serverErrorMessage = '';
        const isFormInvalid = !this.groupFormDataVM.isFormValid(intl);
        if (isFormInvalid) {
            this.groupFormDataVM.allowToShowValidationErrors();
            return false;
        }
        try {
            this.isSaving = true;
            if (roleAction === RolesAction.Create) {
                const params = {
                    ...this.groupFormDataVM.toApiCreateGroupDTO(),
                };
                await this.accountGroupsDAO.createGroup(params);
                showNotification(
                    intl.formatMessage({
                        id: 'oe.accountGroups.groupAdded',
                        description: 'Notification about added group',
                        defaultMessage: 'New group added',
                    })
                );
            } else {
                const params = {
                    accountGroupId: this.accountGroupId,
                    ...this.groupFormDataVM.toApiEditGroupDTO(),
                };
                await this.accountGroupsDAO.updateGroup(params);
                showNotification(
                    intl.formatMessage({
                        id: 'oe.accountGroups.groupUpdated',
                        description: 'Notification about group update',
                        defaultMessage: 'Group updated',
                    })
                );
            }
            this.fetchAccountGroupsList();
            return true;
        } catch (error) {
            this.onCreateGroupError(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    @mobx.action
    private onCreateGroupError(error): void {
        handleApiException(error);
    }

    @mobx.action
    public fetchAccountsList = async (query: string = ''): Promise<void> => {
        try {
            const response = await this.accountsDAO.getAccounts({
                page: 0,
                size: COMMON_ITEMS_PER_PAGE,
                query: query?.trim() || '',
            });
            this.onFetchAccountsListSuccess(response.content);
        } catch (error) {
            if (error.response?.status !== 403) {
                handleApiException(error);
            }
        }
    };

    @mobx.action
    private onFetchAccountsListSuccess(accounts: IAccountDetailsDTO[]): void {
        const formattedResponse = accounts.map(account => ({ ...account, name: `${account.name}` }));

        this.accountsList = formattedResponse;
    }

    @mobx.action
    public onDeleteGroup = (): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.accountGroup.deleteThisAccountGroup'
                            description='Confirm message'
                            defaultMessage='Delete this Account Group'
                        />
                    </span>
                    ?
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.accountGroup.deleteAccountGroup'
                        description='label text'
                        defaultMessage='Delete Account Group'
                    />
                ),
                modifier: 'confirm-warn',
                handler: this.deleteAccountGroupApiCall
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    private deleteAccountGroupApiCall = async (): Promise<void> => {
        try {
                await this.accountGroupsDAO.deleteGroup({ accountGroupId: this.accountGroupId });
                this.completeGroupDeletion(this.accountGroupId);
        } catch (error) {
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        }
    };

    private completeGroupDeletion = (accountGroupId: number): void => {
        this.fetchAccountGroupsList();
        this.closeEditGroupForm(accountGroupId);
    };
}

export { AccountGroupsFormVM };
